import type { AxiosError } from 'axios'

/** Ce întoarce sincronizarea pentru o țară. `synced` apare și lângă `error`: ce s-a scris, s-a scris. */
export interface CountrySyncResult {
  synced?: number
  customer?: number
  supplier?: number
  error?: string
}

export interface SyncResult {
  synced?: number
  customer?: number
  supplier?: number
  perCountry: Record<string, CountrySyncResult>
  /** Cererile de retur (RMA), aduse odata cu comenzile. */
  rma?: { synced?: number; perCountry: Record<string, CountrySyncResult>; error?: string }
}

function line(country: string, r: CountrySyncResult): string {
  if (r.error) {
    // Cate s-au scris inainte de eroare conteaza: altfel pare ca nu s-a intamplat nimic.
    const partial = r.synced ? ` (${r.synced} înainte de eroare)` : ''
    return `${country}: eroare${partial} — ${r.error}`
  }
  if (r.customer !== undefined || r.supplier !== undefined) {
    return `${country}: ${r.customer ?? 0} vânzare, ${r.supplier ?? 0} cumpărare`
  }
  return `${country}: ${r.synced ?? 0}`
}

/** Rezumat pe o linie per țară, pentru toast. */
export function formatSyncResult(result: SyncResult): string {
  const main = Object.entries(result.perCountry).map(([c, r]) => line(c, r))
  if (result.rma) {
    const rma = result.rma.error
      ? [`eroare — ${result.rma.error}`]
      : Object.entries(result.rma.perCountry).map(([c, r]) => line(c, r))
    main.push(`Retururi: ${rma.join('; ')}`)
  }
  return main.join('\n')
}

/**
 * Când o țară eșuează, API-ul întoarce 502, iar totalurile + detaliul per țară vin în
 * `error.details`. Fără asta, utilizatorul ar vedea doar „Sincronizare eșuată pe: BG",
 * fără să afle că RO a mers și câte comenzi au intrat.
 */
export function formatSyncError(err: unknown, fallback = 'Sincronizare eșuată'): string {
  const ax = err as AxiosError<{ error?: { message?: string; details?: SyncResult } }>
  const body = ax.response?.data?.error
  const head = body?.message ?? fallback
  const details = body?.details
  return details?.perCountry ? `${head}\n${formatSyncResult(details)}` : head
}
